import { Component, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactiveJsonRoot } from '@ea-lab/reactive-json'
import { AlertTriangle } from 'lucide-react'
import { builderPlugins } from './builderPlugins'

/**
 * Aperçu FIDÈLE d'une déf de bloc : le composant est réellement monté, avec ses
 * données par défaut, exactement comme le ferait un ajout au canevas.
 *
 * Pourquoi du JSX et pas du YAML (rituel config-first) : le catalogue instancie
 * des rjBuild INCONNUS à la volée, lus dans le registry à l'exécution — aucune
 * capacité reactive-json ne monte un build arbitraire, et ce chrome d'éditeur
 * (empilement de modales, montage paresseux) n'est pas du contenu de page.
 *
 * LE POINT LE MOINS ÉVIDENT — l'aperçu doit fabriquer son propre CONTENEUR DE
 * REQUÊTES. Le responsive des blocs Orbit ne passe pas par les points de
 * rupture d'écran (`md:`, `xl:`) mais par des requêtes de conteneur (`@3xl:`)
 * qui se mesurent sur le plus proche ancêtre `container-type: inline-size` —
 * dans le canevas, c'est `<main id="builder-canvas">` (cf. index.css et
 * `editable/responsiveVisibility.js`). Monté hors de ce conteneur, un bloc
 * n'a AUCUNE requête qui s'applique : il retombe sur ses styles de base,
 * c'est-à-dire sa version mobile — quelle que soit la largeur de la fenêtre.
 * D'où le `.block-preview-viewport` ci-dessous, largeur explicite + conteneur,
 * qui rend l'aperçu fidèle ET rend possible la bascule mobile/tablette/desktop.
 *
 * Trois garde-fous, parce qu'on monte du code qu'on ne contrôle pas :
 *  - ÉTALEMENT : les aperçus se montent au fil de l'eau (à l'approche du
 *    viewport, et de toute façon chacun à son tour), au lieu d'ouvrir d'un coup
 *    toutes les requêtes Elasticsearch des blocs dynamiques. Tout finit monté —
 *    voir `useVisibleOnce` pour pourquoi la visibilité seule ne suffisait pas.
 *  - INERTIE : la couche montée ne reçoit ni clic ni focus (`inert` + CSS) — un
 *    aperçu ne doit pas naviguer ni écrire.
 *  - ISOLEMENT : une déf cassée affiche un encart d'erreur au lieu de faire
 *    tomber tout le catalogue (React démonte l'arbre entier sans frontière).
 */

/** Frontière d'erreur locale : périmètre = UN aperçu. */
class PreviewBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error) {
    console.error('Builder: block preview failed', this.props.type, error)
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="flex h-full items-center justify-center gap-2 p-4 text-xs text-slate-500">
          <AlertTriangle size={14} className="shrink-0" aria-hidden="true" />
          <span>Aperçu indisponible pour ce bloc.</span>
        </div>
      )
    }
    return this.props.children
  }
}

/**
 * Monte l'élément une seule fois, au premier des deux événements suivants :
 * il approche du viewport, OU son tour arrive dans la file de montage.
 *
 * Les DEUX déclencheurs sont nécessaires. La visibilité seule laissait les
 * cadres sous la ligne de flottaison **vides** tant qu'on n'avait pas défilé
 * jusqu'à eux — vus de l'utilisateur, « les premiers s'affichent, les suivants
 * ne se chargent pas ». Le minuteur seul rendrait tout d'un coup, ce qu'on
 * voulait justement éviter (chaque bloc dynamique déclenche sa requête
 * Elasticsearch au montage). Ensemble : ce qu'on regarde s'affiche
 * immédiatement, le reste se remplit tout seul, quelques cadres à la fois.
 *
 * `fallbackMs` est décalé par l'appelant selon le rang du cadre — c'est ce qui
 * étale la file au lieu d'ouvrir toutes les requêtes à la même milliseconde.
 */
const useVisibleOnce = (enabled = true, fallbackMs = 0) => {
  const ref = useRef(null)
  const [visible, setVisible] = useState(!enabled)

  useEffect(() => {
    if (!enabled || visible) return
    const node = ref.current
    if (!node) return

    // Le tour de la file : garantit que TOUT finit par se monter, même sans le
    // moindre défilement.
    const timer = setTimeout(() => setVisible(true), fallbackMs)

    // Navigateur sans IntersectionObserver (ou test JSDOM) : le minuteur suffit.
    if (typeof IntersectionObserver === 'undefined') {
      return () => clearTimeout(timer)
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      // Marge large : un cadre est prêt bien avant d'entrer dans l'écran, donc
      // un défilement rapide ne traverse pas une zone blanche.
      { rootMargin: '600px' }
    )
    observer.observe(node)
    return () => { clearTimeout(timer); observer.disconnect() }
  }, [enabled, visible, fallbackMs])

  return [ref, visible]
}

/**
 * Le `dataOverride` d'une instance neuve du bloc.
 *
 * Même composition qu'un ajout au canevas (BuilderContext.makeBloc +
 * BuilderBlockMount) : les défauts structurels du rjBuild sous les
 * `defaultData` du registry, et une enveloppe `apiData` SANS `submission_url`
 * — un aperçu ne s'enregistre pas, et l'absence d'URL suffit à rendre une
 * unité lecture seule (cf. skill builder, éditabilité = suit la donnée servie).
 */
/**
 * `instanceData` : le contenu RÉEL d'un bloc posé, quand on prévisualise une
 * page existante (le sélecteur de modèles) plutôt qu'une déf du catalogue.
 * Absent ⇒ la graine du catalogue (`defaultData`). Dans les deux cas la donnée
 * s'empile sur le `data:` structurel du rjBuild — même fusion qu'au canevas
 * (cf. BuilderBlockMount), sans quoi les listes déclarées en dur dans la déf
 * (nav, réseaux sociaux) seraient injoignables.
 */
const previewDataOverride = (block, instanceData) => {
  const seed = { ...(block.rjBuild?.data ?? {}), ...(instanceData ?? block.defaultData ?? {}) }
  return {
    ...seed,
    apiData: {
      item_url: null,
      submission_url: null,
      delete_url: null,
      data: { objectData: { builderId: `preview-${block.type}`, data: seed, hiddenOn: [] } },
    },
  }
}

/**
 * Rendu brut d'une déf, sans cadre : utilisé par les deux affichages.
 *
 * Props :
 *  - block      — entrée du registry (`{type, rjBuild, defaultData, …}`)
 *  - lazy       — différer le montage (défaut : vrai)
 *  - fallbackMs — délai au bout duquel le cadre se monte même sans avoir été
 *                 vu (rang dans la file, cf. useVisibleOnce)
 *  - width      — largeur de rendu en px ; le bloc croit disposer de cette
 *                 fenêtre, ce qui permet de le rendre « en grand » puis de le
 *                 réduire (cf. BuilderBlockPreviewFrame)
 *  - onMounted  — notifie le parent que le rjBuild vient d'être instancié
 *                 (le cadre s'en sert pour retirer son voile de chargement)
 */
export const BuilderBlockPreview = ({ block, data, lazy = true, fallbackMs = 0, width, onMounted }) => {
  const [ref, visible] = useVisibleOnce(lazy, fallbackMs)
  const dataOverride = useMemo(() => previewDataOverride(block, data), [block, data])
  const mounted = visible && !!block.rjBuild

  useEffect(() => { if (mounted) onMounted?.() }, [mounted, onMounted])

  return (
    <div ref={ref} className="block-preview-viewport" style={width ? { width } : undefined}>
      {mounted ? (
        <PreviewBoundary type={block.type}>
          <ReactiveJsonRoot
            maybeRawAppRjBuild={block.rjBuild}
            dataOverride={dataOverride}
            plugins={builderPlugins}
          />
        </PreviewBoundary>
      ) : null}
    </div>
  )
}

/**
 * Aperçu réduit dans un cadre (affichage « Rendu »).
 *
 * Le bloc est rendu à `renderWidth` px — la largeur de l'appareil choisi, qui
 * est AUSSI la taille du conteneur de requêtes (cf. en-tête du fichier) — puis
 * mis à l'échelle pour tenir dans la carte. Trois mesures :
 *  - `scale` vient de la largeur RÉELLE du cadre (la grille est fluide, une
 *    valeur en dur désaligne dès que la fenêtre change), **plafonné à 1** :
 *    en aperçu mobile (375 px) dans une carte plus large, agrandir le rendu le
 *    rendrait flou et faux ; on le centre à sa taille réelle ;
 *  - la hauteur du cadre suit celle du contenu une fois réduit, bornée par
 *    `minHeight`/`maxHeight`. Une hauteur fixe donnerait un grand vide blanc
 *    sous un bandeau d'alerte et couperait une section de page ; ici chaque
 *    carte fait la taille de son bloc.
 * La hauteur du contenu se lit sur le nœud NON transformé (`contentRect` de
 * ResizeObserver, comme `offsetHeight`, ignore le `scale` du parent).
 */
export const BuilderBlockPreviewFrame = ({
  block,
  blocks,
  index = 0,
  minHeight = 120,
  maxHeight = 320,
  renderWidth = 1280,
  className = '',
  // Hauteur du contenu à `renderWidth`, AVANT réduction : l'appelant qui doit
  // choisir une disposition (la pop-in avant / après des versions) en a besoin
  // pour savoir quelle place le bloc réclame. Facultatif.
  onContentHeight,
}) => {
  // Un cadre montre SOIT une déf du catalogue (`block`), SOIT une PILE de blocs
  // posés (`blocks`, une page entière — le sélecteur de modèles). Une page
  // n'est qu'une suite de blocs : on les empile dans le même conteneur mis à
  // l'échelle, ce qui donne un aperçu de page sans second composant, et surtout
  // sans dupliquer le conteneur de requêtes (`.block-preview-viewport`) hors
  // duquel tout bloc retombe en version mobile.
  const stack = blocks ?? (block ? [{ block }] : [])
  const frameRef = useRef(null)
  const contentRef = useRef(null)
  const [box, setBox] = useState(null)
  const [contentHeight, setContentHeight] = useState(null)
  const [mounted, setMounted] = useState(false)
  const handleMounted = useCallback(() => setMounted(true), [])

  useEffect(() => {
    const node = frameRef.current
    if (!node) return
    const measure = () => {
      const frameWidth = node.clientWidth
      // Jamais d'agrandissement : un aperçu mobile garde sa taille réelle.
      const s = Math.min(1, frameWidth / renderWidth)
      setBox({ scale: s, offset: Math.max(0, (frameWidth - renderWidth * s) / 2) })
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [renderWidth])

  useEffect(() => {
    const node = contentRef.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(entries => {
      const height = entries[0]?.contentRect?.height
      if (height) {
        setContentHeight(height)
        onContentHeight?.(height)
      }
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [box, onContentHeight])

  const height = box && contentHeight
    ? Math.min(Math.max(contentHeight * box.scale, minHeight), maxHeight)
    : minHeight

  return (
    <div
      ref={frameRef}
      className={`block-preview-frame ${className}`}
      style={{ height }}
      // Hors du parcours clavier : le contenu est décoratif, l'action est
      // portée par les boutons de la carte qui l'entoure.
      inert
      aria-hidden="true"
    >
      {box && (
        <div
          className="block-preview-scale"
          style={{ transform: `translateX(${box.offset}px) scale(${box.scale})` }}
        >
          <div ref={contentRef}>
            {stack.map((entry, i) => (
              <BuilderBlockPreview
                key={entry.key ?? `${entry.block?.type}-${i}`}
                block={entry.block}
                data={entry.data}
                width={renderWidth}
                // Rang dans la file de montage : les premiers cadres partent
                // tout de suite, les suivants s'échelonnent. Plafonné, sinon le
                // 60e bloc d'un gros catalogue attendrait une minute.
                fallbackMs={Math.min(index + i, 24) * 180}
                // Le cadre cesse d'afficher son attente dès que le PREMIER bloc
                // est monté : sur une page longue, attendre le dernier
                // laisserait un spinner par-dessus un aperçu déjà lisible.
                onMounted={i === 0 ? handleMounted : undefined}
              />
            ))}
          </div>
        </div>
      )}
      {/* Tant que le bloc n'est pas instancié, un cadre blanc se lit comme un
          aperçu cassé. On montre qu'il arrive. */}
      {!mounted && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="spinner" />
        </div>
      )}
    </div>
  )
}
